/**
 * BuddyProgramTab + BuddyTile — extracted from HrOnboarding.jsx (Iter 95 refactor).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { HeartHandshake, Coffee, Smile, Meh, Frown, Sparkles, AlertTriangle } from "lucide-react";

function BuddyProgramTab({ wsId }) {
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);
  const load = async () => {
    if (!wsId) return;
    try {
      const [o, f] = await Promise.all([
        api.get(`/workspaces/${wsId}/buddy/overview`),
        api.get(`/workspaces/${wsId}/buddy/vibe-feed?limit=25`),
      ]);
      setData(o.data); setFeed(f.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load Buddy Program")); }
  };
  useEffect(() => { load(); }, [wsId]);

  if (!data) return <Card className="rounded-none"><CardContent className="p-6 text-sm text-muted-foreground">Loading…</CardContent></Card>;

  const { pairs, summary } = data;
  return (
    <div className="space-y-4" data-testid="hr-buddy-tab">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BuddyTile label="Active pairings" value={summary.total_pairs} icon={HeartHandshake} testid="buddy-tile-total" />
        <BuddyTile label="At-risk vibes" value={summary.at_risk} icon={AlertTriangle} tone={summary.at_risk > 0 ? "danger" : null} testid="buddy-tile-atrisk" />
        <BuddyTile label="Stale (14+ days)" value={summary.stale} icon={Coffee} tone={summary.stale > 0 ? "warn" : null} testid="buddy-tile-stale" />
        <BuddyTile label="Healthy" value={summary.healthy} icon={Smile} tone={summary.healthy > 0 ? "ok" : null} testid="buddy-tile-healthy" />
      </div>

      {/* Pairings table */}
      <Card className="rounded-none">
        <CardContent className="p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest font-semibold">Pairings · {pairs.length}</span>
          </div>
          {pairs.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No buddy pairs yet. Assign a <b>buddy</b> when creating employees to activate the program.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {pairs.map((p) => (
                <div key={p.employee_id} className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`buddy-pair-${p.employee_id}`}>
                  <div className="col-span-3">
                    <div className="font-semibold truncate">{p.employee_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{p.designation || "—"}</div>
                  </div>
                  <div className="col-span-3 text-xs">
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Buddy</div>
                    <div className="font-medium truncate">{p.buddy?.name || "—"}</div>
                  </div>
                  <div className="col-span-2 text-xs">
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Last coffee</div>
                    <div className={p.is_stale ? "text-amber-600 font-semibold" : ""}>
                      {p.last_coffee_at ? `${p.coffee_stale_days ?? 0}d ago` : "never"}
                    </div>
                  </div>
                  <div className="col-span-2 text-xs">
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Vibe</div>
                    <div className="flex items-center gap-1">
                      {p.last_vibe_score ? (
                        <>
                          {p.last_vibe_score <= 2 ? <Frown className="w-3.5 h-3.5 text-red-500" /> :
                            p.last_vibe_score <= 3 ? <Meh className="w-3.5 h-3.5 text-amber-500" /> :
                            <Smile className="w-3.5 h-3.5 text-emerald-500" />}
                          <span className={`font-mono ${p.is_at_risk ? "text-red-600 font-bold" : ""}`}>{p.last_vibe_score}/5</span>
                        </>
                      ) : <span className="text-muted-foreground">—</span>}
                    </div>
                  </div>
                  <div className="col-span-2 flex flex-wrap gap-1 justify-end">
                    {p.is_at_risk && <span className="text-[10px] font-semibold px-2 py-0.5 bg-red-500/10 text-red-600 border border-red-500/30">at-risk</span>}
                    {p.is_stale && !p.is_at_risk && <span className="text-[10px] font-semibold px-2 py-0.5 bg-amber-500/10 text-amber-700 border border-amber-500/30">stale</span>}
                    {!p.is_at_risk && !p.is_stale && <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-500/10 text-emerald-700 border border-emerald-500/30">healthy</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Private vibe notes feed (HR only) */}
      <Card className="rounded-none">
        <CardContent className="p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[11px] uppercase tracking-widest font-semibold">Private vibe notes · HR-only</span>
          </div>
          {feed.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No vibe notes yet.</div>
          ) : (
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {feed.map((n) => (
                <div key={n.id} className="px-4 py-3 flex items-start gap-3" data-testid={`buddy-vibe-feed-${n.id}`}>
                  <div className={`w-8 h-8 shrink-0 flex items-center justify-center border ${n.vibe <= 2 ? "border-red-500/40 bg-red-500/10 text-red-600" : n.vibe <= 3 ? "border-amber-500/40 bg-amber-500/10 text-amber-600" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-600"}`}>
                    {n.vibe <= 2 ? <Frown className="w-4 h-4" /> : n.vibe <= 3 ? <Meh className="w-4 h-4" /> : <Smile className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <b>{n.buddy?.name}</b> → <b>{n.employee?.full_name}</b> · <span className="font-mono">{n.vibe}/5</span>
                      {n.hr_flagged && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 bg-red-500/10 text-red-600 border border-red-500/30">flagged</span>}
                    </div>
                    {n.note && <div className="text-sm text-muted-foreground mt-0.5">{n.note}</div>}
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const BUDDY_TILE_TONES = {
  danger: "border-red-500/40 bg-red-500/5",
  warn: "border-amber-500/40 bg-amber-500/5",
  ok: "border-emerald-500/40 bg-emerald-500/5",
};

function BuddyTile({ label, value, icon: Icon, tone, testid }) {
  return (
    <Card className={`rounded-none ${tone ? BUDDY_TILE_TONES[tone] : ""}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</span>
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
        <div className="text-3xl font-bold mt-1.5 tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

export { BuddyProgramTab, BuddyTile };
